/**
 * Ratings API Endpoints
 */

import { ApiService } from '../apiService'
import { ApiResponse } from '../types/common'
import {
  RatingDto,
  RatingDetailedDto,
  CreateRatingRequest,
  UpdateRatingRequest,
  AverageRatingDto,
  PaginatedRatingsResponse,
  RatingsQueryParams,
  RatingTargetType,
} from '../types/ratings'

export const ratingsApi = {
  /**
   * Check if user has rated an adventure
   */
  checkUserRated: (adventureId: string) =>
    ApiService.get<boolean>(`/api/ratings/Adventure/${adventureId}/user-rated`),

  /**
   * Get user's rating for an adventure (null if not rated)
   */
  getUserRating: (adventureId: string) =>
    ApiService.get<RatingDto | null>(`/api/ratings/Adventure/${adventureId}/user-rating`),

  /**
   * Get average rating for an adventure
   */
  getAverageRating: (adventureId: string) =>
    ApiService.get<AverageRatingDto>(`/api/ratings/Adventure/${adventureId}/average`),

  /**
   * Get paginated ratings for an adventure
   */
  getAdventureRatings: (adventureId: string, params?: RatingsQueryParams) => {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.set('page', params.page.toString())
    if (params?.pageSize) queryParams.set('pageSize', params.pageSize.toString())
    if (params?.sortBy) queryParams.set('sortBy', params.sortBy)
    if (params?.sortOrder) queryParams.set('sortOrder', params.sortOrder)
    const queryString = queryParams.toString()
    return ApiService.get<PaginatedRatingsResponse<RatingDto>>(
      `/api/ratings/Adventure/${adventureId}/ratings${queryString ? `?${queryString}` : ''}`
    )
  },

  /**
   * Get paginated detailed ratings for an adventure (with user details)
   */
  getAdventureRatingsDetailed: (adventureId: string, params?: RatingsQueryParams) => {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.set('page', params.page.toString())
    if (params?.pageSize) queryParams.set('pageSize', params.pageSize.toString())
    if (params?.sortBy) queryParams.set('sortBy', params.sortBy)
    if (params?.sortOrder) queryParams.set('sortOrder', params.sortOrder)
    const queryString = queryParams.toString()
    return ApiService.get<PaginatedRatingsResponse<RatingDetailedDto>>(
      `/api/ratings/Adventure/${adventureId}/ratings-detailed${queryString ? `?${queryString}` : ''}`
    )
  },

  /**
   * Generic method to get ratings for any target type
   */
  getRatings: (targetType: RatingTargetType, targetId: string, params?: RatingsQueryParams) => {
    const targetTypeName = RatingTargetType[targetType]
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.set('page', params.page.toString())
    if (params?.pageSize) queryParams.set('pageSize', params.pageSize.toString())
    if (params?.sortBy) queryParams.set('sortBy', params.sortBy)
    if (params?.sortOrder) queryParams.set('sortOrder', params.sortOrder)
    const queryString = queryParams.toString()
    return ApiService.get<PaginatedRatingsResponse<RatingDto>>(
      `/api/ratings/${targetTypeName}/${targetId}/ratings${queryString ? `?${queryString}` : ''}`
    )
  },

  /**
   * Submit/create a new rating
   */
  submitRating: (data: CreateRatingRequest) =>
    ApiService.post<ApiResponse<RatingDto>>('/api/ratings', data),

  /**
   * Update an existing rating
   */
  updateRating: (ratingId: string, data: UpdateRatingRequest) =>
    ApiService.put<ApiResponse<RatingDto>>(`/api/ratings/${ratingId}`, data),

  /**
   * Delete a rating
   */
  deleteRating: (ratingId: string) =>
    ApiService.delete<ApiResponse<void>>(`/api/ratings/${ratingId}`),
}
